import type { Graphics } from 'pixi.js';

// Composed as a canvas composes its transforms: Pixi's own methods compose the other way round.
export class Pen {
  private a = 1;
  private b = 0;
  private c = 0;
  private d = 1;
  private x = 0;
  private y = 0;
  private readonly saved: number[] = [];

  readonly g: Graphics;

  constructor(g: Graphics) {
    this.g = g;
  }

  clear(): this {
    this.g.clear();
    this.saved.length = 0;
    this.set(1, 0, 0, 1, 0, 0);
    return this;
  }

  save(): this {
    this.saved.push(this.a, this.b, this.c, this.d, this.x, this.y);
    return this;
  }

  restore(): this {
    const at = this.saved.length - 6;
    if (at < 0) {
      throw new Error('a pen restored more than it saved');
    }
    const [a = 1, b = 0, c = 0, d = 1, x = 0, y = 0] = this.saved.splice(at, 6);
    return this.set(a, b, c, d, x, y);
  }

  translate(x: number, y: number): this {
    return this.set(
      this.a,
      this.b,
      this.c,
      this.d,
      this.a * x + this.c * y + this.x,
      this.b * x + this.d * y + this.y,
    );
  }

  rotate(angle: number): this {
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    return this.set(
      this.a * cos + this.c * sin,
      this.b * cos + this.d * sin,
      this.c * cos - this.a * sin,
      this.d * cos - this.b * sin,
      this.x,
      this.y,
    );
  }

  scale(factor: number): this {
    return this.set(
      this.a * factor,
      this.b * factor,
      this.c * factor,
      this.d * factor,
      this.x,
      this.y,
    );
  }

  private set(a: number, b: number, c: number, d: number, x: number, y: number): this {
    this.a = a;
    this.b = b;
    this.c = c;
    this.d = d;
    this.x = x;
    this.y = y;
    this.g.setTransform(a, b, c, d, x, y);
    return this;
  }
}
